<script setup lang="ts">
import WidgetPanels from "@/components/widget/WidgetPanels.vue";
import CommonFilter from "@/components/common/CommonFilter.vue";
import CommonTable from "@/components/common/CommonTable.vue";
import CommonPagination from "@/components/common/CommonPagination.vue";
import {useMessageStore} from "@/features/message/message.store";
import {useSavedMessagesStore} from "@/features/saved/saved.store";
import {useAppStore} from "@/store/app.store";
import {IChat, IChatItem} from "@/features/chat/chat.types";
import {IPaginationMeta} from "@/features/pagination/pagination.types";
import ReplyTime from "@/features/message/ui/replyTime.vue";
import {ref, watch} from "vue";
import {DateTime} from "luxon";
import {ArrowNarrowDownLeft, ArrowNarrowUpRight, Bookmark, Clock, Edit02, Trash01, Image01} from "utruckin-icons";
import {ISavedMessage} from "@/features/saved/saved.types";
import {useAlert} from "@/utils/alert";
import VoiceMessage from "@/features/message/ui/voiceMessage.vue";
import DocumentMessage from "@/features/message/ui/documentMessage.vue";
import VideoMessage from "@/features/message/ui/videoMessage.vue";
import CallMessage from "@/features/message/ui/callMessage.vue";
import EditedMessage from "@/features/message/ui/editedMessage.vue";
import {IMessageHistory} from "@/types/main";
import CommonButton from "@/components/common/CommonButton.vue";
import CommonModal from "@/components/common/CommonModal.vue";
import router from "@/router";

const messageStore = useMessageStore();
const appStore = useAppStore();
const savedMessageStore = useSavedMessagesStore();
const {show} = useAlert();

const apiBaseUrl = process.env.VUE_APP_API_BASE_URL

const loading = ref<boolean>(false);
const list = ref<IChat>();
const isFetching = ref<boolean>(false);
const paginationData = ref<IPaginationMeta>();
const historyModal = ref<boolean>(false);
const messageHistory = ref<IMessageHistory[]>([])

const getChats = async () => {
  if (isFetching.value) return

  try {
    isFetching.value = true
    list.value = await messageStore.getMessages(loading)
    if (list.value) {
      paginationData.value = {
        has_more: list.value.has_more,
        next_page: list.value.next_page,
        page_size: list.value.page_size,
        page: list.value.page,
        pages: list.value.pages,
        prev_page: list.value.prev_page,
        total: list.value.total,
      }
    }

  } finally {
    isFetching.value = false
  }
}

const handlePageChange = (page: number) => {
  messageStore.commonFilter.page = page;
}

const handleLimitChange = (limit: number) => {
  messageStore.commonFilter.page = 1;
  messageStore.commonFilter.page_size = limit;
}

const formatDate = (value: string): string => {
  return DateTime.fromFormat(value, 'yyyy-MM-dd HH:mm:ss', {
    zone: 'utc',
  })
      .setZone('America/New_York')
      .toFormat('MMM d - HH:mm')
}

const saveMessage = async (item: IChatItem, event: MouseEvent) => {
  event.stopPropagation()
  let savedMessage: ISavedMessage = {
    item_type: item.item_type,
    is_saved: !item.is_saved,
    message_id: item.message_id,
    msg_id: item.msg_id,
    chat_id: item.chat_id,
    account_id: item.account_id,
    call_id: item.call_id
  }

  let data = await savedMessageStore.updateSavedMessage(loading, savedMessage)

  if (data) {
    show("Saved successfully", "success");
  }

  if (data) {
    appStore.triggerRefresh()
  }
}

const rowClick = (row: any,) => {
  router.push({path: `/chat/${row.chat_id}`, query: {chat: row.account_id}});
}

const imageClick = (item: any, event: any) => {
  event.stopPropagation()
  window.open(`${apiBaseUrl}${item.media_path}`, "_blank");
}

const closeModal = () => {
  messageHistory.value = []
}

const showHistoryModal = (history: IMessageHistory[], event: MouseEvent) => {
  event.stopPropagation()
  historyModal.value = true;
  messageHistory.value = history;
}

const closeHistoryModal = () => {
  historyModal.value = false;
  closeModal();
}

const formatHistoryDate = (value: string) => {
  let newValue;
  if (value.includes("T")) {
    newValue = DateTime.fromISO(value, {
      zone: 'utc',
    })
        .setZone('America/New_York')
        .toFormat('yyyy-MM-dd HH:mm:ss')
  } else {
    newValue = DateTime.fromFormat(value, 'yyyy-MM-dd HH:mm:ss', {
      zone: 'utc',
    })
        .setZone('America/New_York')
        .toFormat('yyyy-MM-dd HH:mm:ss')
  }

  let time: string;

  time = newValue.split(" ")[1];

  return time.split(":")[0] + ":" + time.split(":")[1] + ":" + time.split(":")[2];
}

watch(
    [
      () => appStore.refreshKey,
      () => JSON.stringify(messageStore.commonFilter),
      () => JSON.stringify(messageStore.additionalFilter),
    ],
    () => {
      getChats()
    },
    {immediate: true}
)
</script>

<template>
  <div class="v-message">
    <WidgetPanels
        type="messages"
        :store="messageStore"
        :filter="messageStore.commonFilter"
    />

    <div class="body">
      <CommonFilter :loading="loading" :store="messageStore"/>

      <CommonTable
          striped
          :items="list?.items"
          :loading="loading"
          :headers="messageStore.headers"
          @row-clicked="rowClick"
      >
        <template #id="{index}">
          {{ (list.page - 1) * list.page_size + index + 1 }}
        </template>

        <template #activity_time="{item}">
          {{ formatDate(item.activity_time) }}
        </template>

        <template #reply_time_seconds="{item}">
          <div v-if="item.reply_time_seconds">
            <ReplyTime :time="item.reply_time_seconds"/>
          </div>
          <div v-else>
            -
          </div>
        </template>

        <template #from="{item}">
          <div class="from_icon" :class="item.is_outgoing ? 'outgoing' : ''">
            <ArrowNarrowUpRight v-if="item.is_outgoing" :size="16"/>
            <ArrowNarrowDownLeft v-else :size="16"/>
          </div>
        </template>

        <template #text="{item}">
          <div class="message">
            <div class="is_deleted" v-if="item.is_deleted">
              <Trash01 :size="16"/>
            </div>

            <div class="reply__time" v-else-if="item.reply_time_seconds && item.reply_time_seconds > 300">
              <Clock :size="16"/>
            </div>

            <div class="is_edited" v-else-if="item.is_edited && item.msg_type !== 'document'">
              <Edit02 :size="16"/>
            </div>
            <div v-if="item.msg_type == 'photo'">
              <div class="photo" @click="(e: any) => imageClick(item, e)">
                <Image01 :size="16"/>
              </div>
            </div>
            <div v-if="item.msg_type == 'audio'">
              <VoiceMessage :audio-url="`${apiBaseUrl}${item.media_path}`"/>
            </div>
            <div class="message__document" v-if="item.msg_type == 'document'">
              <DocumentMessage :caption="item.caption" :document-url="`${apiBaseUrl}${item.media_path}`"/>
            </div>
            <div v-if="item.msg_type == 'video'">
              <VideoMessage :document-url="`${apiBaseUrl}${item.media_path}`"/>
            </div>
            <div v-if="item.item_type == 'call'">
              <CallMessage :item="item"/>
            </div>
            <EditedMessage
                v-if="item.is_edited && item.msg_type !== 'document'"
                @show-history="showHistoryModal"
                :history="item.history"
                :last-text="item.text"
                :last-time="item.activity_time"
                :last-id="item.id"
            />
            <span class="text" v-else-if="item.msg_type == 'text'">{{ item.text }}</span>
          </div>
        </template>

        <template #save="{item}">
          <div class="save__icon" @click="(e: MouseEvent) => {saveMessage(item, e)}">
            <Bookmark v-if="item.is_saved" class="filled" :size="16"/>
            <Bookmark v-else :size="16"/>
          </div>
        </template>
      </CommonTable>
    </div>
    <CommonPagination
        :meta="paginationData"
        @change-page="handlePageChange"
        @change-limit="handleLimitChange"
    />
  </div>

  <CommonModal
      v-model="historyModal"
      @close="closeModal"
  >
    <div class="history">
      <div class="history__item" v-for="(item, index) in messageHistory" :key="index">
        <div class="history__item--date">{{ formatHistoryDate(item.edited_at) }}</div>
        <div class="history__item--message" lang="en">{{ item.old_text }}</div>
      </div>
    </div>

    <template #footer>
      <div class="modal__footer">
        <CommonButton
            type="primary"
            label="Close"
            @clicked="closeHistoryModal"
        />
      </div>
    </template>
  </CommonModal>
</template>

<style lang="scss">
.v-message {
  .body {
    padding: 1rem 1rem 0 1rem;
    height: calc(100vh - 267px);

    .from_icon {
      width: 26px;
      height: 26px;
      border-radius: 50%;
      border: 1px solid var(--color-border);
      display: flex;
      justify-content: center;
      align-items: center;
      color: var(--color-label);
      background: var(--color-background);

      &.outgoing {
        background: var(--color-bg-secondary);
      }
    }

    .message {
      display: flex;
      align-items: center;
      gap: 12px;
      width: 600px;
      overflow: hidden;
      white-space: nowrap;

      .text {
        width: 100%;
        display: block;
        overflow: hidden;
        white-space: nowrap;
        mask-image: linear-gradient(to right, black 80%, transparent 100%);
      }

      .reply__time, .is_deleted, .is_edited {
        min-width: 32px;
        min-height: 32px;
        display: flex;
        justify-content: center;
        align-items: center;
        background: var(--color-red);
        color: var(--color-white);
        border-radius: 50%;
      }

      .is_edited {
        background: var(--color-orange);
      }

      &__document {
        width: 100%;
      }

      .photo {
        width: 38px;
        height: 38px;
        border: 1px solid var(--color-border);
        border-radius: 12px;
        background: var(--color-bg-secondary);
        color: var(--color-label);
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }

    .save__icon {
      width: 31px;
      height: 31px;
      background: var(--color-bg-secondary);
      border: 1px solid var(--color-border);
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;

      &:hover {
        background: var(--color-hover);
      }

      .filled {
        fill: var(--color-primary);
      }
    }
  }
}

.history {
  max-height: 500px;
  overflow-y: auto;
  padding: 16px 16px 0 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__item {
    display: flex;
    gap: 8px;
    align-items: flex-end;

    &--date {
      width: 11%;
      color: var(--color-secondary-label);
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      text-align: right;
    }

    &--message {
      width: 89%;
      overflow-wrap: break-word;
      word-break: break-word;
      hyphens: auto;
      color: var(--color-label);
      background: var(--color-bg-secondary);
      padding: 12px;
      border-radius: 14px;
      font-size: 14px;
      line-height: 20px;
      font-weight: 500;
      letter-spacing: 0;
    }
  }
}

.modal__footer {
  display: flex;
  width: 100%;
  justify-content: flex-end;
}
</style>
